import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./Studio.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

/* --- ImageModal --- */
function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="st-modal-overlay" onClick={onClose}>
      <div className="st-modal" onClick={(e) => e.stopPropagation()}>
        <button className="st-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="st-modal__image" />
        <span className="st-modal__label">{label}</span>
      </div>
    </div>
  );
}

/* Resize image for Vercel 4.5 MB body limit */
const resizeImage = (file, maxDim = 2048) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width <= maxDim && height <= maxDim && file.size < 3_500_000) {
        resolve(file);
        return;
      }
      const scale = Math.min(maxDim / width, maxDim / height, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob), "image/png");
    };
    img.src = URL.createObjectURL(file);
  });

const MODELS = [
  { id: "sdxl", label: "SDXL (Juggernaut)" },
  { id: "flux2", label: "Flux2.Dev" },
];

const RATIOS = ["1:1", "3:4", "4:3", "9:16", "16:9"];
const RESOLUTIONS = ["512", "768", "1024"];

export default function StudioCharacter() {
  /* --- Asset list --- */
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState("");

  /* --- Reference images --- */
  const [faceRef, setFaceRef] = useState(null);       // main face image (File blob URL)
  const [detailRefs, setDetailRefs] = useState([]);    // detail images (tattoos, piercings, etc.)
  const faceInputRef = useRef(null);
  const detailInputRef = useRef(null);

  /* --- Prompts --- */
  const [idea, setIdea] = useState("");
  const [prompter, setPrompter] = useState("");

  /* --- Settings --- */
  const [model, setModel] = useState("sdxl");
  const [ratio, setRatio] = useState("1:1");
  const [resolution, setResolution] = useState("1024");
  const [seed, setSeed] = useState("");

  /* --- Generation state --- */
  const [phase, setPhase] = useState("face"); // "face" | "body"
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalSrc, setModalSrc] = useState(null);
  const pollingRef = useRef(false);

  /* --- Saved views --- */
  const [savedViews, setSavedViews] = useState([]);

  /* --- Reel refs --- */
  const charsReelRef = useRef(null);
  const viewsReelRef = useRef(null);
  const detailReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  /* --- Load characters on mount --- */
  const loadCharacters = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/characters`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      const chars = data.characters || [];
      const detailed = await Promise.all(
        chars.map(async (c) => {
          const cid = c.id || c;
          try {
            const r = await fetch(`${API_URL}/characters/${cid}`, {
              headers: { Authorization: `Bearer ${getToken()}` },
            });
            const d = await r.json();
            return {
              id: cid,
              displayName: d.display_name || cid,
              faceUrl: d?.face?.reference?.[0]?.url || null,
            };
          } catch {
            return { id: cid, displayName: cid, faceUrl: null };
          }
        })
      );
      setCharacters(detailed);
    } catch {}
  }, []);

  useEffect(() => { loadCharacters(); }, [loadCharacters]);

  /* --- Load saved views when character changes --- */
  useEffect(() => {
    if (!selectedChar) { setSavedViews([]); return; }
    fetch(`${API_URL}/characters/${selectedChar}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const face = d?.face?.views || [];
        const body = d?.body?.views || [];
        setSavedViews([...face, ...body]);
      })
      .catch(() => setSavedViews([]));
  }, [selectedChar]);

  /* --- File handlers --- */
  const handleFaceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const resized = await resizeImage(file);
    setFaceRef(URL.createObjectURL(resized));
    if (faceInputRef.current) faceInputRef.current.value = "";
  };

  const handleDetailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const resized = await resizeImage(file);
    setDetailRefs((prev) => [...prev, URL.createObjectURL(resized)]);
    if (detailInputRef.current) detailInputRef.current.value = "";
  };

  const removeDetail = (idx) => {
    setDetailRefs((prev) => prev.filter((_, i) => i !== idx));
  };

  /* --- Convert blob URL to base64 --- */
  const blobToB64 = async (blobUrl) => {
    const resp = await fetch(blobUrl);
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.readAsDataURL(blob);
    });
  };

  /* --- Poll job status --- */
  const pollJob = useCallback(async (jobId) => {
    pollingRef.current = true;
    for (let i = 0; i < 120; i++) {
      if (!pollingRef.current) return;
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(`${API_URL}/studio/character/${encodeURIComponent(jobId)}/status`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.status === "completed") {
          const rr = await fetch(`${API_URL}/studio/character/${encodeURIComponent(jobId)}/result`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const rd = await rr.json();
          if (rd.image) setResult(rd.image);
          setStatus("done");
          pollingRef.current = false;
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar");
          setStatus("error");
          pollingRef.current = false;
          return;
        }
      } catch (err) { console.error("Poll error:", err); }
    }
    setErrorMsg("Tiempo agotado");
    setStatus("error");
    pollingRef.current = false;
  }, []);

  /* --- Generate --- */
  const canGenerate = (faceRef || idea.trim()) && status !== "generating";

  const handleGenerate = async () => {
    setStatus("generating");
    setResult(null);
    setErrorMsg("");

    try {
      const body = {
        phase,
        idea,
        prompter,
        model,
        ratio,
        resolution,
        seed,
        character_id: selectedChar,
        face_ref_b64: faceRef ? await blobToB64(faceRef) : "",
        detail_refs_b64: await Promise.all(detailRefs.map(blobToB64)),
      };
      const res = await fetch(`${API_URL}/studio/character/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      pollJob(data.job_id);
    } catch (err) {
      setErrorMsg(err.message || "Error al generar");
      setStatus("error");
    }
  };

  const getResultSrc = () => {
    if (!result) return null;
    return result.startsWith("data:") ? result : `data:image/png;base64,${result}`;
  };

  return (
    <div className="studio-page">
      {/* LEFT PANEL — Refs + Prompts */}
      <div className="st-panel st-panel--left">
        <div className="st-header">
          <h2 className="st-panel__title">Crear Personaje Base</h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className={`st-btn-secondary ${phase === "face" ? "" : "st-btn-secondary--inactive"}`}
              onClick={() => setPhase("face")}
              style={phase === "face" ? { background: "var(--turquesa)", color: "var(--dark)" } : {}}
            >
              Fase A: Cara
            </button>
            <button
              className={`st-btn-secondary ${phase === "body" ? "" : "st-btn-secondary--inactive"}`}
              onClick={() => setPhase("body")}
              style={phase === "body" ? { background: "var(--turquesa)", color: "var(--dark)" } : {}}
            >
              Fase B: Cuerpo
            </button>
            <button
              className={`st-btn-primary ${status === "generating" ? "st-btn-primary--loading" : ""}`}
              disabled={!canGenerate}
              onClick={handleGenerate}
            >
              {status === "generating" ? "Generando..." : "Generar Vistas"}
            </button>
          </div>
        </div>

        {/* Characters reel */}
        <section className="st-section">
          <span className="st-section__title">Personajes existentes</span>
          <div className="st-reel">
            <button className="st-reel__btn" onClick={() => scrollReel(charsReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={charsReelRef}>
              <div className="st-reel-item st-reel-item--add" onClick={() => { setSelectedChar(""); setFaceRef(null); setDetailRefs([]); setIdea(""); setPrompter(""); }}>
                +
              </div>
              {characters.map((c) => (
                <div
                  key={c.id}
                  className={`st-reel-item ${selectedChar === c.id ? "st-reel-item--selected" : ""}`}
                  onClick={() => setSelectedChar(c.id)}
                >
                  {c.faceUrl ? <img src={c.faceUrl} alt={c.displayName} /> : <div className="st-reel-item__placeholder" />}
                  <span className="st-reel-item__name">{c.displayName}</span>
                </div>
              ))}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(charsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Face reference upload */}
        <section className="st-section">
          <span className="st-section__title">Imagen de referencia (cara)</span>
          <div
            className="st-upload-zone"
            style={faceRef ? { minHeight: 160 } : {}}
            onClick={() => !faceRef && faceInputRef.current?.click()}
          >
            {faceRef ? (
              <>
                <img src={faceRef} alt="Face ref" />
                <button className="st-upload-zone__remove" onClick={(e) => { e.stopPropagation(); setFaceRef(null); }}>&times;</button>
              </>
            ) : (
              <span>Haz clic para cargar imagen de cara</span>
            )}
          </div>
          <input ref={faceInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFaceUpload} />
        </section>

        {/* Detail references reel */}
        <section className="st-section">
          <span className="st-section__title">Detalles (tatuajes, piercings, etc.)</span>
          <div className="st-reel">
            <button className="st-reel__btn" onClick={() => scrollReel(detailReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={detailReelRef}>
              <div className="st-reel-item st-reel-item--add" onClick={() => detailInputRef.current?.click()}>+</div>
              {detailRefs.map((url, i) => (
                <div key={i} className="st-reel-item" onClick={() => removeDetail(i)}>
                  <img src={url} alt={`Detail ${i + 1}`} />
                  <span className="st-reel-item__name">x quitar</span>
                </div>
              ))}
              {detailRefs.length === 0 && <span className="st-reel-empty">Sin detalles</span>}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(detailReelRef, 1)}>{"\u25B6"}</button>
          </div>
          <input ref={detailInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleDetailUpload} />
        </section>

        {/* Prompts */}
        <section className="st-section">
          <span className="st-section__title">Idea</span>
          <textarea
            className="st-textarea"
            placeholder="Describe tu personaje... (ej: hombre 30 anos, pelo oscuro rizado, barba corta)"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
          />
        </section>

        <section className="st-section">
          <span className="st-section__title">Prompter (prompt tecnico)</span>
          <textarea
            className="st-textarea"
            placeholder="Se generara automaticamente o editalo manualmente..."
            value={prompter}
            onChange={(e) => setPrompter(e.target.value)}
          />
        </section>

        {/* Saved views reel */}
        {selectedChar && (
          <section className="st-section">
            <span className="st-section__title">Vistas guardadas</span>
            <div className="st-reel">
              <button className="st-reel__btn" onClick={() => scrollReel(viewsReelRef, -1)}>{"\u25C0"}</button>
              <div className="st-reel__frames" ref={viewsReelRef}>
                {savedViews.length > 0 ? (
                  savedViews.map((v, i) => (
                    <div key={v.url || i} className="st-reel-item" onClick={() => setModalSrc(v.url)}>
                      <img src={v.url} alt={v.name || `Vista ${i + 1}`} />
                      <span className="st-reel-item__name">{v.name || `Vista ${i + 1}`}</span>
                    </div>
                  ))
                ) : (
                  <span className="st-reel-empty">Genera y guarda vistas</span>
                )}
              </div>
              <button className="st-reel__btn" onClick={() => scrollReel(viewsReelRef, 1)}>{"\u25B6"}</button>
            </div>
          </section>
        )}

        {/* Bottom bar */}
        <div className="st-bottom-bar">
          <div className="st-select-group">
            <span className="st-select-group__label">Motor</span>
            <select className="st-select" value={model} onChange={(e) => setModel(e.target.value)}>
              {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Ratio</span>
            <select className="st-select" value={ratio} onChange={(e) => setRatio(e.target.value)}>
              {RATIOS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Resolucion</span>
            <select className="st-select" value={resolution} onChange={(e) => setResolution(e.target.value)}>
              {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}px</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Semilla</span>
            <input className="st-seed-input" placeholder="random" value={seed} onChange={(e) => setSeed(e.target.value)} />
          </div>
          <span className="st-credits">Creditos est.: --</span>
        </div>

        {errorMsg && <div className="st-error">{errorMsg}</div>}
      </div>

      {/* RIGHT PANEL — Result */}
      <div className="st-panel st-panel--right">
        <h2 className="st-panel__title">Resultado</h2>
        <div className="st-result">
          <div
            className={`st-result__image ${result ? "st-result__image--loaded" : ""}`}
            onClick={() => result && setModalSrc(getResultSrc())}
          >
            {result ? (
              <img src={getResultSrc()} alt="Resultado" />
            ) : (
              <div className="st-results-empty">
                {status === "generating" ? (
                  <span className="st-spinner" />
                ) : (
                  <p>{phase === "face" ? "Carga una cara o escribe un prompt y pulsa Generar" : "Genera la cara primero, luego pasa a Fase B"}</p>
                )}
              </div>
            )}
          </div>
          {result && (
            <div className="st-result__footer">
              <span className="st-result__label">{phase === "face" ? "Cara" : "Cuerpo"}</span>
              <div className="st-result__actions">
                <button className="st-btn-secondary">Guardar</button>
                <button className="st-btn-download">&#x2B73;</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {modalSrc && <ImageModal src={modalSrc} label="Vista" onClose={() => setModalSrc(null)} />}
    </div>
  );
}
